import type { CatalogPayload } from "../lib/types/catalog";
import type { DemoTrip, EventsPayload, SitePayload, WhatIf } from "../lib/types/demo";
import type { TopologyPayload } from "../lib/types/topology";
import { useState } from "react";

import { useIlopaNote } from "../lib/notes";
import { useUpdateNote } from "../lib/api";
import { adbUplink } from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { AvailMode, ViewMode } from "../lib/viewMode";
import {
  IncidentBody, SeatAvailability, SeatSiteCard, SitePanel, TripPanel,
} from "./AvailabilityPanel";
import ZonePanel from "./ZonePanel";
import { resolveZone } from "../lib/zone";
import type { ResolvedZone } from "../lib/zone";


interface SidebarProps {
  topology: TopologyPayload;
  catalog: CatalogPayload;
  selection?: Selection;
  onClose?: () => void;
  /** "panel" = desktop fixed-width column; "sheet" = fluid, for MobileSheet. */
  variant?: "panel" | "sheet";
  /** Zone chain cards live here now (moved out of the SVG canvas). */
  onSelect?: (s: Selection | undefined) => void;
  viewMode?: ViewMode;
  /** Desktop panel width (drag-resizable in FleetPage). */
  width?: number;
  /** Availability view: the active demo trip (what-if simulated). */
  availTrip?: DemoTrip;
  /** Availability sub-mode + the 30-day site rollup for Site mode. */
  availMode?: AvailMode;
  site?: SitePayload;
  /** Fleet-specific event catalog (demo). Optional pre-filled search. */
  events?: EventsPayload;
  onOpenCatalog?: (query?: string) => void;
  /** What-if simulation wiring (exclusion toggles + reset). */
  whatIf?: WhatIf;
  /** Trip navigation: prev/next through the site's 30-trip window. */
  tripNav?: {
    index: number;
    count: number;
    onPrev: () => void;
    onNext: () => void;
    onBackToSite: () => void;
  };
  /** Deep-dive jump used by the Site panel (trend bars, worst trips…). */
  onTripSelect?: (tripId: string) => void;
}


export default function Sidebar({
  topology, catalog, selection, onClose, variant = "panel",
  onSelect, viewMode, width = 420,
  availTrip, availMode = "trip", site, events, onOpenCatalog, whatIf,
  tripNav, onTripSelect,
}: SidebarProps) {
  const isAvail = viewMode === "avail";
  const isSite = isAvail && availMode === "site" && site !== undefined;
  return (
    <aside
      className={
        variant === "sheet"
          ? "w-full bg-white px-4 pb-4 pt-1"
          : "flex-shrink-0 overflow-auto bg-white p-6"
      }
      style={variant === "sheet"
        ? undefined
        : { width, boxShadow: "-1px 0 24px rgba(15, 23, 42, 0.04)" }}
    >
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h1 className="m-0 text-base font-bold leading-tight tracking-tight text-slate-900">
          {selectionTitle(selection, topology)}
        </h1>
        {selection && onClose && (
          <button
            onClick={onClose}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            clear
          </button>
        )}
      </header>

      {selection && selection.kind === "seat" && (
        <p className="mb-4 text-xs leading-relaxed text-slate-500">
          {selectionSub(selection, topology)}
        </p>
      )}
      {selection && selection.kind !== "seat" && (
        <SubNote noteKey={subNoteKey(selection, topology)} />
      )}

      {!selection && (
        isSite
          ? <SitePanel site={site!} onSelect={onSelect}
                       onTripSelect={onTripSelect}
                       onEventSearch={onOpenCatalog} />
          : isAvail && availTrip
          ? <TripPanel trip={availTrip} onSelect={onSelect} whatIf={whatIf}
                       tripNav={tripNav} />
          : (
            <p className="text-sm text-slate-500">
              Click anything in the LOPA — a seat, port, NC, MCU, CS, FS, AI,
              or zone card. The detail panel will show what you clicked plus
              the rest of its chain.
            </p>
          )
      )}

      {selection?.kind === "incident" && availTrip && (
        <IncidentBody trip={availTrip} idx={selection.idx} onSelect={onSelect} />
      )}

      {selection?.kind === "seat" && (
        <>
          {isSite && (
            <SeatSiteCard site={site!} seatId={selection.seatId} />
          )}
          {isAvail && !isSite && availTrip && (
            <SeatAvailability
              trip={availTrip}
              seatId={selection.seatId}
              events={events}
              onOpenCatalog={onOpenCatalog}
              whatIf={whatIf}
            />
          )}
          <SeatBody topology={topology} catalog={catalog} seatId={selection.seatId} />
        </>
      )}
      {selection?.kind === "nc" && (
        <NCBody topology={topology} ncId={selection.id} />
      )}
      {selection?.kind === "port" && (
        <PortBody topology={topology} nc={selection.nc} port={selection.port} />
      )}
      {selection?.kind === "cs" && (
        <>
          <SimpleBody label="Content Server" id={selection.id}
                      sub="336-002 · Content Server" />
          <NoteSection noteKey="cs" />
        </>
      )}
      {selection?.kind === "fs" && (
        <FSBody topology={topology} num={selection.idx + 1} />
      )}
      {selection?.kind === "ai" && (
        <AIBody topology={topology} num={selection.idx + 1} />
      )}
      {selection?.kind === "aiport" && (
        <AIPortBody topology={topology} unit={selection.unit}
                    num={selection.num} port={selection.port} />
      )}
      {selection?.kind === "adb" && (
        <ADBBody topology={topology} num={selection.num} />
      )}
      {selection?.kind === "dport" && (
        <DPortBody topology={topology} num={selection.num} port={selection.port} />
      )}
      {viewMode !== undefined && viewMode !== "avail" && (
        <ZonePanel
          topology={topology}
          selection={selection}
          onSelect={onSelect}
          viewMode={viewMode}
        />
      )}
    </aside>
  );
}


function DPortBody({
  topology, num, port,
}: { topology: TopologyPayload; num: number; port: number }) {
  const seatsHere = Object.values(topology.seats).filter(
    (s) => s.chain_num === num && s.chain_port === port,
  );
  const kind = seatsHere[0]?.chain_kind ?? "FS";
  const sebs = [...new Set(
    seatsHere.map((s) => s.chain_seb).filter((x): x is number => x !== undefined),
  )].sort((a, b) => a - b);
  return (
    <>
      <Section title="Data chain port">
        <Row k="Parent" v={`${kind} ${num}`} />
        <Row k="Port"   v={port} />
        <Row k="Seats"  v={seatsHere.length} />
        <Row k="SEBs"   v={sebs.length ? `${sebs.length} (${sebs[0]}–${sebs[sebs.length - 1]})` : "—"} />
      </Section>
      {seatsHere.length > 0 && (
        <Section title="Seats on this chain">
          <p className="text-xs text-slate-600">
            {seatsHere.map((s) => `${s.row}${s.letter}`).sort().join(", ")}
          </p>
        </Section>
      )}
      <NoteSection noteKey="dport" />
    </>
  );
}


function ADBBody({
  topology, num,
}: { topology: TopologyPayload; num: number }) {
  const lru = (topology.lrus ?? []).find(
    (l) => l.acronym?.startsWith("ADB") &&
           parseInt(l.designation.match(/(\d+)\s*$/)?.[1] ?? "0", 10) === num,
  );
  const seatsHere = Object.values(topology.seats).filter(
    (s) => s.chain_kind === "ADB" && s.chain_num === num,
  );
  const portsHere = [...new Set(
    seatsHere.map((s) => s.chain_port).filter((p): p is number => p !== undefined),
  )].sort((a, b) => a - b);
  return (
    <>
      <Section title="Area Distribution Box">
        <Row k="Designation" v={lru?.designation ?? `ADB ${num}`} />
        <Row k="IP" v={lru?.ip} ip />
        <Row k="Uplink" v={lru?.server_type && lru.server_number != null
          ? `${lru.server_type} ${lru.server_number}${lru.server_port != null ? ` · Port ${lru.server_port}` : ""}`
          : "—"} />
        <Row k="Seats" v={seatsHere.length} />
        <Row k="Seat ports" v={portsHere.join(", ")} />
      </Section>
      <NoteSection noteKey="adb" />
    </>
  );
}


// eX1 MCU values are 0-based internally; hardware designations are 1-based.
function ncDisplay(isEx3: boolean, n: number): number {
  return isEx3 ? n : n + 1;
}

function selectionTitle(s: Selection | undefined, t: TopologyPayload): string {
  if (!s) return "Selection";
  const isEx3 = t.layout.topology.type === "nc-port";
  const ncLabel = isEx3 ? "NC" : "MCU";
  switch (s.kind) {
    case "seat": return `Seat ${s.seatId}`;
    case "nc":   return `${ncLabel} ${ncDisplay(isEx3, s.id)}`;
    case "port": {
      const resolved = resolveZone(t, s.nc, s.port);
      const prefix = resolved && resolved.index >= 0
        ? `Zone ${resolved.index + 1} · `
        : "";
      return `${prefix}${ncLabel} ${ncDisplay(isEx3, s.nc)} · Port ${s.port}`;
    }
    case "cs":   return `CS ${s.id}`;
    case "fs":   return `FS ${s.idx + 1}`;
    case "ai":   return `AI ${s.idx + 1}`;
    case "aiport": return `${s.unit} ${s.num} · Port ${s.port}`;
    case "adb":  return `ADB ${s.num}`;
    case "dport": {
      const seat = Object.values(t.seats).find(
        (x) => x.chain_num === s.num && x.chain_port === s.port,
      );
      return `${seat?.chain_kind ?? "FS"} ${s.num} · Port ${s.port}`;
    }
    case "incident": return `Incident #${s.idx + 1}`;
  }
}

// Seat subtitle is computed DATA (cabin / parent / port) — everything
// else's descriptor wording comes from ilopa_notes.csv via subNoteKey.
function selectionSub(s: Selection, t: TopologyPayload): string {
  if (s.kind !== "seat") return "";
  const isEx3 = t.layout.topology.type === "nc-port";
  const seat = t.seats[s.seatId];
  if (!seat) return "";
  const nc = isEx3 ? `NC ${seat.nc}` : `MCU ${(seat.mcu ?? 0) + 1}`;
  return `${seat.cabin || "—"} · ${nc} / Port ${seat.port}`;
}


/** Selection kind → its descriptor row in ilopa_notes.csv. */
function subNoteKey(s: Selection, t: TopologyPayload): string {
  const isEx3 = t.layout.topology.type === "nc-port";
  switch (s.kind) {
    case "nc":       return isEx3 ? "desc_nc_ex3" : "desc_mcu";
    case "port":     return "desc_port";
    case "cs":       return "desc_cs";
    case "fs":       return "desc_fs";
    case "ai":       return "desc_ai";
    case "aiport":   return "desc_aiport";
    case "adb":      return "desc_adb";
    case "dport":    return "desc_dport";
    case "incident": return "desc_incident";
    default:         return "";
  }
}


/** Panel subtitle — latest stored version, editable in place. */
function SubNote({ noteKey }: { noteKey: string }) {
  if (!noteKey) return null;
  return (
    <div className="mb-4">
      <NoteText noteKey={noteKey} variant="subtitle" />
    </div>
  );
}


function SeatBody({
  topology, catalog, seatId,
}: { topology: TopologyPayload; catalog: CatalogPayload; seatId: string }) {
  const seat = topology.seats[seatId];
  const isEx3 = topology.layout.topology.type === "nc-port";
  if (!seat) {
    return <p className="text-sm text-slate-500">Seat {seatId} not found.</p>;
  }
  const seatNc = isEx3 ? seat.nc : seat.mcu;
  const resolved = seatNc !== undefined
    ? resolveZone(topology, seatNc, seat.port)
    : null;
  const zone = resolved?.zone;
  const zoneTitle = resolved
    ? (resolved.index >= 0
        ? `Zone ${resolved.index + 1} · ${isEx3 ? "NC" : "MCU"} ${ncDisplay(isEx3, zone!.nc)} / Port ${zone!.port}`
        : `${isEx3 ? "NC" : "MCU"} ${ncDisplay(isEx3, zone!.nc)} / Port ${zone!.port}`)
    : "";
  return (
    <>
      <Section title="Routing">
        {isEx3 ? (
          <>
            <Row k="NC"   v={seat.nc} />
            <Row k="Port" v={seat.port} />
            <Row k="HPS"  v={seat.hps ?? "—"} />
          </>
        ) : (
          <>
            <Row k="MCU"  v={seat.mcu !== undefined && seat.mcu !== null ? seat.mcu + 1 : "—"} />
            <Row k="Port" v={seat.port} />
          </>
        )}
      </Section>
      {seat.chain_kind && (
        <Section title="Data chain">
          {seat.chain_kind === "ADB" && seat.chain_num !== undefined && (() => {
            const u = adbUplink(topology, seat.chain_num!);
            return (
              <Row k="File Server"
                   v={u.fs !== null
                     ? `FS ${u.fs}${u.port !== null ? ` · Port ${u.port}` : ""}`
                     : "—"} />
            );
          })()}
          <Row k="Parent" v={`${seat.chain_kind} ${seat.chain_num}`} />
          <Row k={`${seat.chain_kind} port`} v={seat.chain_port} />
          {seat.chain_col !== undefined && seat.chain_col !== null && (
            <Row k="Col" v={seat.chain_col} />
          )}
          <Row k="SEB position" v={seat.chain_seb} />
          <InlineNote noteKey="seat_chain" />
        </Section>
      )}
      {zone && (
        <ZoneSection title={zoneTitle} zone={zone} />
      )}
      <Section title="Hardware">
        <Row k="SEB"        v={seat.seb_number} />
        <Row k="SEB IP"     v={seat.seb_ip}     ip />
        <Row k="A/C SEB IP" v={seat.ac_seb_ip}  ip />
        <Row k="SDU IP"     v={seat.sdu_ip}     ip />
        <Row k="SPM IP"     v={seat.spm_ip}     ip />
        <Row k="SPM #"      v={seat.spm_number} />
      </Section>
      <Section title="Per-seat options">
        <Row k="Audio feedback" v={seat.audio_feedback} />
        <Row k="Stowed display" v={seat.stowed_display} />
        <Row k="Handset"        v={seat.handset} />
        <Row k="Group"          v={seat.seat_group} />
      </Section>
      <Section title="Catalog">
        <p className="text-xs text-slate-600">
          <b className="text-slate-900">{Object.keys(catalog.events).length}</b>{" "}
          events apply to this fleet ({catalog.fleet_config_string}).
        </p>
        <InlineNote noteKey="catalog_note" />
      </Section>
    </>
  );
}


function NCBody({
  topology, ncId,
}: { topology: TopologyPayload; ncId: number }) {
  const isEx3 = topology.layout.topology.type === "nc-port";
  const seatsHere = Object.values(topology.seats).filter(
    (s) => (isEx3 ? s.nc : s.mcu) === ncId,
  );
  const portsHere = [...new Set(seatsHere.map((s) => s.port))].sort((a, b) => a - b);
  const zonesHere = topology.zones.filter((z) => z.nc === ncId);

  return (
    <>
      <Section title={isEx3 ? "Network Controller" : "MCU"}>
        <Row k="ID"    v={isEx3 ? ncId : ncId + 1} />
        <Row k="Seats" v={seatsHere.length} />
        <Row k="Ports" v={portsHere.join(", ")} />
      </Section>
      {zonesHere.length > 0 && (
        <Section title={`Zones on this ${isEx3 ? "NC" : "MCU"}`}>
          {zonesHere.map((z) => (
            <Row
              key={`${z.nc}-${z.port}`}
              k={`Port ${z.port}`}
              v={`${z.seat_count} seats · ${z.hps_count} HPS`}
            />
          ))}
        </Section>
      )}
      <NoteSection noteKey={isEx3 ? "nc_ex3" : "mcu"} />
    </>
  );
}


function PortBody({
  topology, nc, port,
}: { topology: TopologyPayload; nc: number; port: number }) {
  const isEx3 = topology.layout.topology.type === "nc-port";
  const resolved = resolveZone(topology, nc, port);
  const zone = resolved?.zone;
  const zoneTitle = resolved
    ? (resolved.index >= 0
        ? `Zone ${resolved.index + 1} · ${isEx3 ? "NC" : "MCU"} ${ncDisplay(isEx3, nc)} / Port ${port}`
        : `${isEx3 ? "NC" : "MCU"} ${ncDisplay(isEx3, nc)} / Port ${port}`)
    : "";
  const seatsOnThisPort = Object.values(topology.seats).filter(
    (s) => (isEx3 ? s.nc : s.mcu) === nc && s.port === port,
  );

  return (
    <>
      {zone && (
        <ZoneSection title={zoneTitle} zone={zone} />
      )}
      <Section title="Port">
        <Row k={isEx3 ? "NC" : "MCU"} v={ncDisplay(isEx3, nc)} />
        <Row k="Port"  v={port} />
        <Row k="Cabin" v={zone?.cabin || "—"} />
        <Row k="Seats" v={seatsOnThisPort.length} />
        <Row k="HPS"   v={zone?.hps_count ?? "—"} />
      </Section>
      {seatsOnThisPort.length > 0 && (
        <Section title="Seats">
          <p className="text-xs text-slate-600">
            {seatsOnThisPort
              .map((s) => `${s.row}${s.letter}`)
              .sort()
              .join(", ")}
          </p>
        </Section>
      )}
    </>
  );
}


// Out-port socket detail: which units are cabled off this port (primary
// rows from lru_lookup; redundant iLOPA_Flag links listed separately).
function AIPortBody({
  topology, unit, num, port,
}: { topology: TopologyPayload; unit: string; num: number; port: number }) {
  const lrus = topology.lrus ?? [];
  const matches = (t?: string | null, n?: number | null, p?: number | null) =>
    !!t && t.toUpperCase().startsWith(unit.toUpperCase()) && n === num && p === port;
  const children = lrus.filter(
    (l) => matches(l.server_type, l.server_number, l.server_port),
  );
  const linked = (topology.lru_links ?? []).filter(
    (l) => matches(l.parent_type, l.parent_num, l.parent_port),
  );
  return (
    <>
      <Section title="Output port">
        <Row k="Unit" v={`${unit} ${num}`} />
        <Row k="Port" v={port} />
        <Row k="Feeds" v={children.length || "—"} />
      </Section>
      {children.length > 0 && (
        <Section title="Cabled to">
          {children.map((l) => (
            <Row key={l.designation} k={l.designation}
                 v={l.lru_port != null ? `in P${l.lru_port}` : ""} />
          ))}
        </Section>
      )}
      {linked.length > 0 && (
        <Section title="Redundant links">
          {linked.map((l) => (
            <Row key={`lk-${l.designation}`} k={l.designation}
                 v={l.lru_port != null ? `in P${l.lru_port}` : ""} />
          ))}
          <InlineNote noteKey="redundant_link" withSource />
        </Section>
      )}
      <NoteSection noteKey="aiport" />
    </>
  );
}


// Aircraft Interface detail. The "AI n HCS08" LRU row is the unit's internal
// HCS08 microcontroller module (placeholder IP, no ports/children) — hidden
// from the head-end tree, surfaced here instead. Gets a dedicated box/link
// on the LOPA if per-fleet parent data lands later.
function AIBody({ topology, num }: { topology: TopologyPayload; num: number }) {
  const lrus = topology.lrus ?? [];
  const unit = lrus.find((l) => l.designation === `AI ${String(num).padStart(3, "0")}`);
  const hcs08 = lrus.find(
    (l) => l.acronym.includes("HCS08") && l.designation.startsWith(`AI ${num} `),
  );
  const typeCode = (l: typeof unit): string =>
    l?.lru_type != null
      ? `${l.lru_type}-${String(l.lru_subtype ?? 0).padStart(3, "0")}`
      : "—";
  return (
    <>
      <Section title="Aircraft Interface">
        <Row k="Index" v={num} />
        <Row k="Type" v={typeCode(unit)} />
        {unit?.ip && <Row k="IP" v={unit.ip} ip />}
        {unit?.physical_location && <Row k="Location" v={unit.physical_location} />}
      </Section>
      {hcs08 && (
        <Section title="HCS08 module">
          <Row k="Designation" v={hcs08.designation} />
          <Row k="Type" v={typeCode(hcs08)} />
          <InlineNote noteKey="hcs08" />
        </Section>
      )}
      <NoteSection noteKey="ai" />
    </>
  );
}


function SimpleBody({
  label, id, sub,
}: { label: string; id: number; sub: string }) {
  return (
    <Section title={label}>
      <Row k="Index" v={id} />
      <Row k="Model" v={sub} />
    </Section>
  );
}


/** Behaviour / provenance copy — every displayed string is a versioned
 *  note (CSV baseline = v1; UI edits append versions on the backend and
 *  the latest one renders). Rows without wording show a placeholder. */
function NoteSection({
  noteKey, title = "Behaviour",
}: { noteKey: string; title?: string }) {
  const { note } = useIlopaNote(noteKey);
  if (!note) return null;
  return (
    <Section title={title}>
      <NoteText noteKey={noteKey} variant="card" />
    </Section>
  );
}


/** Small in-section note (hint line under rows). */
function InlineNote({
  noteKey, withSource = false,
}: { noteKey: string; withSource?: boolean }) {
  return <NoteText noteKey={noteKey} variant="inline" withSource={withSource} />;
}


/**
 * Editable UI-copy slot. Displays the latest version of the note; the ✎
 * button (shown when the notes API is reachable) opens an in-place editor
 * whose save appends a NEW version to the store — nothing is overwritten,
 * and the UI immediately shows the new latest.
 */
function NoteText({
  noteKey, variant, withSource = true,
}: {
  noteKey: string;
  variant: "card" | "inline" | "subtitle";
  withSource?: boolean;
}) {
  const { note, editable } = useIlopaNote(noteKey);
  const update = useUpdateNote();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftSource, setDraftSource] = useState("");
  if (!note) return null;
  const pending = note.text.trim().length === 0;

  const startEdit = () => {
    setDraft(note.text);
    setDraftSource(note.source ?? "");
    setEditing(true);
  };
  const save = () => {
    update.mutate(
      { key: noteKey, text: draft.trim(), source: draftSource.trim() || undefined },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (editing) {
    return (
      <div className="rounded border border-sky-300 bg-sky-50/40 p-2">
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Displayed text…"
          className="w-full resize-y rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline focus:outline-2 focus:outline-sky-400"
        />
        <input
          value={draftSource}
          onChange={(e) => setDraftSource(e.target.value)}
          placeholder="Source (where this claim comes from)"
          className="mt-1.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[10px] text-slate-600 focus:outline focus:outline-2 focus:outline-sky-400"
        />
        <div className="mt-1.5 flex items-center gap-2">
          <button
            onClick={save}
            disabled={update.isPending}
            className="rounded bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {update.isPending ? "Saving…" : `Save as v${(note.version ?? 1) + 1}`}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100"
          >
            Cancel
          </button>
          {update.isError && (
            <span className="text-[10px] text-red-600">Save failed</span>
          )}
        </div>
      </div>
    );
  }

  const pencil = editable ? (
    <button
      onClick={startEdit}
      title={`Edit this text (currently v${note.version ?? 1})`}
      className="ml-1 rounded px-1 text-[10px] text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-sky-600 group-hover:opacity-100"
    >
      ✎
    </button>
  ) : null;
  const versionChip = (note.version ?? 1) > 1 ? (
    <span
      className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-semibold text-slate-400"
      title="Edited in the UI — version history is stored"
    >
      v{note.version}
    </span>
  ) : null;

  if (pending) {
    return (
      <div className="group rounded border border-dashed border-slate-300 bg-slate-50/60 px-2.5 py-2">
        <p className="text-[11px] italic text-slate-400">
          Description pending{pencil}
        </p>
        <p className="mt-0.5 text-[9.5px] text-slate-300">
          ilopa_notes · {noteKey}
        </p>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className="group">
        <p className="text-xs text-slate-600">
          {note.text}
          {versionChip}
          {pencil}
        </p>
        {note.source && (
          <p className="mt-1 text-[10px] italic text-slate-400">
            Source: {note.source}
          </p>
        )}
      </div>
    );
  }
  if (variant === "subtitle") {
    return (
      <p className="group text-xs leading-relaxed text-slate-500">
        {note.text}
        {versionChip}
        {pencil}
      </p>
    );
  }
  return (
    <p className="group mt-1 text-[10px] italic text-slate-400">
      {note.text}
      {withSource && note.source ? <> Source: {note.source}</> : null}
      {versionChip}
      {pencil}
    </p>
  );
}


/** File-server detail from lru_lookup (real type code / IP / uplink). */
function FSBody({ topology, num }: { topology: TopologyPayload; num: number }) {
  const lru = (topology.lrus ?? []).find((l) => {
    const a = l.acronym ?? "";
    const isServer =
      a.startsWith("FS") || (a.startsWith("IS") && !a.includes("HCS08"));
    return isServer &&
      parseInt(l.designation.match(/(\d+)\s*$/)?.[1] ?? "0", 10) === num;
  });
  const typeCode = lru?.lru_type != null
    ? `${lru.lru_type}-${String(lru.lru_subtype ?? 0).padStart(3, "0")}`
    : "—";
  return (
    <>
      <Section title="File Server">
        <Row k="Designation" v={lru?.designation ?? `FS ${num}`} />
        <Row k="Type" v={typeCode} />
        {lru?.ip && <Row k="IP" v={lru.ip} ip />}
        <Row k="Uplink" v={lru?.server_type && lru.server_number != null
          ? `${lru.server_type} ${lru.server_number}${lru.server_port != null ? ` · Port ${lru.server_port}` : ""}`
          : "—"} />
      </Section>
      <NoteSection noteKey="fs" />
    </>
  );
}


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <h2 className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h2>
      <div className="mb-2 rounded border-l-[3px] border-slate-300 bg-slate-50 p-3 text-xs text-slate-800 shadow-sm transition hover:shadow">
        {children}
      </div>
    </>
  );
}


function ZoneSection({
  title, zone,
}: {
  title: string;
  zone: ResolvedZone;
}) {
  const hasChain = Boolean(zone.sib_start);
  const hasRedundancy = zone.redundant_nc !== 0;
  return (
    <>
      <h2 className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Zone
      </h2>
      <div
        className="mb-2 rounded bg-white p-3 text-xs text-slate-800 shadow-sm"
        style={{ borderLeft: `4px solid ${zone.color}` }}
      >
        <div className="mb-2 text-[13px] font-bold text-slate-900">{title}</div>
        <div className="mb-2 text-xs text-slate-500">
          {zone.cabin || "—"} · {zone.seat_count} seats · {zone.hps_count} HPS
        </div>
        {hasChain && (
          <>
            <div className="font-mono text-[11px] text-slate-700">
              SIB {zone.sib_start_seb}  {zone.sib_start}
            </div>
            {zone.sib_end && zone.sib_end !== zone.sib_start && (
              <div className="font-mono text-[11px] text-slate-700">
                SIB {zone.sib_end_seb}  {zone.sib_end}
              </div>
            )}
          </>
        )}
        {hasRedundancy && (
          <div className="mt-2 text-[11px] text-slate-500">
            Redundant →{" "}
            <span className="font-semibold text-slate-700">
              NC {zone.redundant_nc} / P{zone.redundant_port}
            </span>
          </div>
        )}
        {zone.synthesized && <InlineNote noteKey="zone_synthesized" />}
      </div>
    </>
  );
}


function Row({
  k, v, ip,
}: { k: string; v: unknown; ip?: boolean }) {
  const display = v === undefined || v === null || v === "" ? "—" : String(v);
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-slate-500">{k}</span>
      {ip && display !== "—" ? (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
          {display}
        </span>
      ) : (
        <span className="font-semibold text-slate-900">{display}</span>
      )}
    </div>
  );
}
